import React, { useCallback, useMemo, useState } from 'react';
import { Button, Expandable, Stack, Text } from '@moda/om';
import { take, groupBy } from 'ramda';
import { useFeatureFlag, SECONDS_TO_MILLISECONDS } from '@moda/portal-stanchions';
import { LineItemList } from '../../components/LineItemList';
import { LineItem } from '../../hooks/useCart';
import { LineItemGrid } from '../../components/LineItem';
import { GridSortByDropdown } from '../../components/GridPeripherals/GridSortByDropdown';
import { GridItemCount } from '../../components/GridPeripherals/GridItemCount';
import { SAVED_FOR_LATED_OPTION } from '../../constants';
import { usePLPParams } from '../../hooks/usePLPParams';

import './ShoppingBagPageSavedItems.scss';

const PAGE_SIZE = 10;

interface Props {
  savedItems: LineItem[];
}

export const ShoppingBagPageSavedItems: React.FC<Props> = ({ savedItems }) => {
  const [numberOfShownItems, setNumberOfShownItems] = useState(PAGE_SIZE);
  const availableSorts = SAVED_FOR_LATED_OPTION;
  const {
    params: { sort },
    currentSort,
    changeSort
  } = usePLPParams(availableSorts);

  const items = useMemo(
    () => take(numberOfShownItems, savedItems),
    [numberOfShownItems, savedItems]
  );

  const handleClick = useCallback(() => {
    setNumberOfShownItems(numberOfShownItems + PAGE_SIZE);
  }, [numberOfShownItems]);

  const sortedItems = useMemo(() => {
    const sortByDateAdded = (items: LineItem[]) =>
      items.sort(
        (firstItem, secondItem) =>
          new Date(secondItem.attributes.createdAt || '').getTime() -
          new Date(firstItem.attributes.createdAt || '').getTime()
      );

    const { available, soldOut } = groupBy(
      item => (item.attributes.isSoldOut ? 'soldOut' : 'available'),
      items
    );

    const sortedGroupedItems = [
      ...sortByDateAdded(available || []),
      ...sortByDateAdded(soldOut || [])
    ];

    const activeTrunkshowFirst = sortedGroupedItems.sort(item => {
      const isActiveTrunkshow = item.attributes?.activeEndAt
        ? item.attributes.activeEndAt * SECONDS_TO_MILLISECONDS > Date.now()
        : false;
      return isActiveTrunkshow ? -1 : 1;
    });

    const inActiveTrunkshowLast = activeTrunkshowFirst.sort(item => {
      const isActiveTrunkshow = item.attributes?.activeEndAt
        ? item.attributes.activeEndAt * SECONDS_TO_MILLISECONDS < Date.now()
        : false;
      return isActiveTrunkshow ? -1 : 1;
    });

    switch (sort) {
      case 'in_stock':
        return sortByDateAdded(available || []);
      case 'sold_out':
        return sortByDateAdded(soldOut || []);
      case 'recently_added':
        return sortByDateAdded(items || []);
      default:
        return inActiveTrunkshowLast;
    }
  }, [items, sort]);

  const useNewSavedForLater = useFeatureFlag('new-saved-for-later', [
    { default: false },
    { if: true, then: true }
  ]);

  if (savedItems.length === 0) return null;

  if (useNewSavedForLater) {
    return (
      <Stack className="ShoppingBagPageSavedItems" space={1}>
        <div className="ShoppingBagPageSavedItems__header">
          <Text
            className="ShoppingBagPageSavedItems__header--title"
            as="h1"
            family="serif"
            treatment="h4"
          >
            Saved for Later
          </Text>
          <GridItemCount
            className="ShoppingBagPageSavedItems__header--count"
            count={sortedItems?.length || 0}
          />

          <GridSortByDropdown
            className="ShoppingBagPageSavedItems__header--sort"
            currentSort={currentSort}
            availableSorts={availableSorts}
            onSortByChange={option => changeSort(option)}
            smallTextMobile
          />
        </div>
        <div className="ShoppingBagPageSavedItems__savedForLater">
          {sortedItems.map((item, index) => (
            <LineItemGrid
              className="LineItemGrid__button-wide"
              key={item.id}
              item={item}
              index={index}
            />
          ))}
        </div>
      </Stack>
    );
  }

  return (
    <Expandable
      className="ShoppingBagPageSavedItems"
      name={
        <Text family="serif" treatment="h4">
          Saved For Later
        </Text>
      }
    >
      <LineItemList items={items} />

      {numberOfShownItems < savedItems.length && (
        <div className="ShoppingBagPageSavedItems__footer">
          <Button onClick={handleClick}>Load more</Button>

          <Text className="ShoppingBagPageSavedItems__footer-text">
            {`SHOWING 1-${numberOfShownItems} OF ${savedItems.length} SAVED ITEMS`}{' '}
          </Text>
        </div>
      )}
    </Expandable>
  );
};
